import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated } from '../../utils/authSession';
import { clearSessionNative } from '../../utils/nativeSessionStore';
import { useLocationHeartbeat } from '../../hooks/useLocationHeartbeat';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';

const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const location = useLocation();
  const [suspended, setSuspended] = useState(false);

  // Report the customer's live location every ~2s while the app is open (only
  // while logged in - this component is inside the authed area - and only while
  // foregrounded; the hook stops itself when the app is backgrounded/closed).
  useLocationHeartbeat();

  // Even with a cached login, verify the account isn't suspended - on app open
  // and whenever it returns to the foreground. If it is, drop the session and
  // bounce to the login screen, which shows the suspended notice + reason.
  useEffect(() => {
    const check = () => {
      if (document.hidden) return;
      const token = localStorage.getItem('accessToken');
      if (!token) return;
      fetch(`${API_URL}/auth/status`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d?.status === 'suspended') {
            localStorage.setItem('suspended', '1');
            localStorage.setItem('suspendedReason', d.reason || '');
            ['accessToken', 'refreshToken', 'supabaseToken', 'user', 'driver'].forEach((k) => localStorage.removeItem(k));
            clearSessionNative();
            setSuspended(true);
          }
        })
        .catch(() => {});
    };
    check();
    document.addEventListener('visibilitychange', check);
    return () => document.removeEventListener('visibilitychange', check);
  }, []);

  if (suspended || !isAuthenticated()) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
};

export default ProtectedRoute;
